import { clamp01, Node, _decorator } from 'cc';
import Lobby from '../../../../System/Lobby';
import YouTubePlayerController from '../../../Controller/UIController/YouTubePlayerController';
import BaseBanner, { BannerEventType } from './BaseBanner';
import Page from '../../../../../common/scripts/Toolkit/Components/Page/Page';
import Delay from '../../../../../common/scripts/Toolkit/Utils/Delay';
import DataType from '../../../../../tableEngine/Script/Game/Data/Config/DataStructure';
import NodeName from '../../../Data/Config/SceneNodeName';

const { ccclass, property } = _decorator;

@ccclass('YouTubePlayerBanner')
export default class YouTubePlayerBanner extends BaseBanner {
    @property(YouTubePlayerController)
    private readonly playerController: YouTubePlayerController = null;

    private _onStateChangeCallback: (event: YT.OnStateChangeEvent) => void;

    public onLoad() {
        this._onStateChangeCallback = this._onStateChange.bind(this);
        this.playerController.addEventListener('onStateChange', this._onStateChangeCallback);
    }

    public start() {
        this._adjustVideoRenderOrder();
    }

    public onEnable() {
        Page.EVENT_TARGET.on(Page.EVENT_TYPE.VISIBLE_PAGE_COUNT_CHANGED, this._adjustVideoRenderOrder, this);
        this.node.parent.on(Node.EventType.TRANSFORM_CHANGED, this._updateClipPath, this);
        this._updateClipPath();
        this._adjustVideoRenderOrder();
    }

    public onDisable() {
        Page.EVENT_TARGET.off(Page.EVENT_TYPE.VISIBLE_PAGE_COUNT_CHANGED, this._adjustVideoRenderOrder, this);
        this.node.parent.off(Node.EventType.TRANSFORM_CHANGED, this._updateClipPath, this);
    }

    public onDestroy() {
        this.playerController.removeEventListener('onStateChange', this._onStateChangeCallback);
    }

    public display({ VideoURL }: DataType.Hall.BannerData): void {
        if (!VideoURL) {
            throw new Error('VideoURL is required');
        }
        const videoID = /(?:v=|\/)([0-9A-Za-z_-]{11})/.exec(VideoURL)?.[1];
        if (!videoID) {
            throw new Error(`Invalid VideoURL: ${VideoURL}`);
        }
        this.playerController.cueVideoById(videoID);
    }

    protected shouldTurningBlocked(): boolean {
        if (!this.playerController.isReady()) {
            return false;
        }
        const currentState = this.playerController.player.getPlayerState();
        return currentState === YT.PlayerState.PLAYING || currentState === YT.PlayerState.BUFFERING;
    }

    protected onCanPlayChanged(): void {
        if (this._canPlay) {
            if (this.playerController.isReady()) {
                if (this.playerController.player.getPlayerState() === YT.PlayerState.ENDED) {
                    this.playerController.seekTo(0, false);
                }
                this.playerController.playVideo();
            } else {
                // play video after player is cued
                Delay.event(
                    YouTubePlayerController.EVENT_TYPE.STATE_CHANGE,
                    this.playerController,
                    (event: YT.OnStateChangeEvent) => event.data === YT.PlayerState.CUED,
                ).then(() => {
                    this.playerController.playVideo();
                });
            }
        } else {
            this.playerController.pauseVideo();
        }
    }

    protected onInteractableChanged(): void {
        this._adjustVideoRenderOrder();
    }

    private _adjustVideoRenderOrder() {
        let zIndex = 1;
        if (Page.isAnyPageVisible) {
            const notificationPopupNode = Lobby.scene.tryGetNode(NodeName.NotificationPopup);
            if (Page.INSTANCES.some((page) => page.isVisible && (!notificationPopupNode || page.node !== notificationPopupNode))) {
                zIndex = -1;
            }
        }
        if (!this.interactable) {
            zIndex = -1;
        }
        this.playerController.bindDomNode.element.style.zIndex = zIndex.toString();
    }

    private _updateClipPath() {
        const { bindDomNode } = this.playerController;
        const videoWidth = bindDomNode.node.transformWidth;
        const worldPositionX = bindDomNode.node.worldPosition.x;
        let clipPath = 'none';
        if (worldPositionX < 0) {
            const unseenLeftPercentage = clamp01((worldPositionX * -1) / videoWidth) * 100;
            clipPath = `polygon(${unseenLeftPercentage}% 0%, 100% 0%, 100% 100%, ${unseenLeftPercentage}% 100%)`;
        } else if (worldPositionX + videoWidth > 720) {
            const unseenRightPercentage = clamp01((worldPositionX + videoWidth - 720) / videoWidth) * 100;
            clipPath = `polygon(0% 0%, ${100 - unseenRightPercentage}% 0%, ${100 - unseenRightPercentage}% 100%, 0% 100%)`;
        }
        bindDomNode.element.style.clipPath = clipPath;
        bindDomNode.element.style['-webkit-clip-path'] = clipPath;
    }

    private _onStateChange(event: YT.OnStateChangeEvent) {
        if (event.data === YT.PlayerState.ENDED) {
            this.node.emit(BannerEventType.TURN);
        }
    }
}
